import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { rollbackJournal } from './journalRollback'
import { logAction } from './auditLog'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const rpc = (fn: string, args: Record<string, unknown>): any => (supabase as any).rpc(fn, args)

export function resolveContactName(
  contact: { business_name?: string | null; name?: string | null; company_name?: string | null } | null | undefined,
  fallback: string = 'Unknown'
): string {
  return (
    contact?.business_name?.trim() ||
    contact?.company_name?.trim() ||
    contact?.name?.trim() ||
    fallback
  ).trim()
}

async function getAccount(tenantId: string, mappingKey: string): Promise<string | null> {
  const { data } = await db('account_mappings')
    .select('account_id')
    .eq('tenant_id', tenantId)
    .eq('mapping_key', mappingKey)
    .single()
  return data?.account_id ?? null
}

// Resolve GL account IDs for a tenant by account_mappings key(s), e.g.
// ['accounts_payable','payable'] or ['accounts_receivable','receivable']. This is
// the tenant-safe replacement for hardcoded `code IN (...)` lookups: a tenant that
// renumbers its chart of accounts keeps working because the mapping, not the code,
// is the source of truth. `.in()` (not `.single()`) lets the fallback key resolve
// without throwing. Empty array means the tenant has no such mapping — callers
// treat that as a graceful zero, matching the previous missing-account behaviour.
export async function resolveAccountsByMappingKeys(
  client: SupabaseClient,
  tenantId: string,
  keys: string[]
): Promise<string[]> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data } = await (client as any)
    .from('account_mappings')
    .select('account_id')
    .eq('tenant_id', tenantId)
    .in('mapping_key', keys)
  return ((data ?? []) as Array<{ account_id: string | null }>)
    .map(m => m.account_id)
    .filter((id): id is string => Boolean(id))
}

// Balance of a GL account (by code) for a tenant. Sums debit - credit over
// journal_entry_lines. Filter semantics match get_trial_balance (migration 071):
// COALESCE(is_voided,false)=false AND COALESCE(is_posted,true)=true, and reversed
// entries are NOT excluded (the reversal entry nets them out), so this reconciles
// exactly to the Trial Balance / Balance Sheet figure for the same account.
export async function getAccountBalance(
  tenantId: string,
  accountCode: string,
  options?: { location_id?: string },
): Promise<number> {
  const { data: account } = await db('accounts')
    .select('id')
    .eq('tenant_id', tenantId)
    .eq('code', accountCode)
    .maybeSingle()
  if (!account?.id) return 0

  let balance = 0
  const PAGE = 1000
  let from = 0
  for (;;) {
    let query = db('journal_entry_lines')
      .select('debit_amount, credit_amount, journal_entries!inner(is_voided, is_posted)')
      .eq('tenant_id', tenantId)
      .eq('account_id', account.id)
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1)
    if (options?.location_id) query = query.eq('location_id', options.location_id)

    const { data, error } = await query
    if (error) throw error
    const rows = (data ?? []) as Array<{
      debit_amount: number | null
      credit_amount: number | null
      journal_entries: { is_voided: boolean | null; is_posted: boolean | null } | null
    }>
    for (const row of rows) {
      const je = row.journal_entries
      if (je?.is_voided === true) continue
      if (je?.is_posted === false) continue
      balance += Number(row.debit_amount ?? 0) - Number(row.credit_amount ?? 0)
    }
    if (rows.length < PAGE) break
    from += PAGE
  }
  return balance
}

function getPaymentMappingKey(method: string): string {
  const map: Record<string, string> = {
    cash: 'cash',
    bank_transfer: 'bank',
    bank: 'bank',
    pos: 'pos',
    card: 'pos',
    mobile_money: 'mobile_money',
  }
  return map[method?.toLowerCase()] ?? 'cash'
}

type JournalLine = {
  accountId: string
  description?: string
  debit: number
  credit: number
  contactType?: string
  contactId?: string
  contactName?: string
  paymentAccountId?: string
}

async function createJournalEntry(params: {
  tenantId: string
  date: string
  description: string
  entryType: string
  sourceType?: string
  sourceId?: string
  createdBy?: string
  idempotencyKey?: string
  lines: JournalLine[]
}): Promise<string | null> {
  // ── Validation ────────────────────────────────────────────────────────────
  if (params.lines.length < 2) {
    console.error('[Accounting] Journal entry must have at least 2 lines')
    throw new Error('Journal entry must have at least 2 lines')
  }

  const totalDebit = params.lines.reduce((s, l) => s + (l.debit ?? 0), 0)
  const totalCredit = params.lines.reduce((s, l) => s + (l.credit ?? 0), 0)
  if (Math.abs(totalDebit - totalCredit) > 0.01) {
    console.error('[Accounting] UNBALANCED ENTRY REJECTED', { totalDebit, totalCredit, lines: params.lines })
    throw new Error('Journal entry rejected: debits do not equal credits')
  }

  // ── Period locking check ──────────────────────────────────────────────────
  try {
    const entryYear = new Date(params.date).getFullYear()
    const entryMonth = new Date(params.date).getMonth() + 1
    const { data: locked } = await db('locked_periods')
      .select('id')
      .eq('tenant_id', params.tenantId)
      .eq('period_year', entryYear)
      .eq('period_month', entryMonth)
      .maybeSingle()
    if (locked) {
      throw new Error(`Period ${entryMonth}/${entryYear} is locked. Cannot post entries to a locked period.`)
    }
  } catch (e) {
    // Re-throw period lock errors; swallow table-not-found errors
    if (e instanceof Error && e.message.includes('locked')) throw e
  }

  const { data: numData, error: numErr } = await rpc('next_journal_number', {
    p_tenant_id: params.tenantId,
  })
  if (numErr || numData == null) throw new Error(`Journal number generation failed: ${numErr?.message ?? 'RPC returned null'}`)

  const { data: entry, error } = await db('journal_entries')
    .insert({
      tenant_id: params.tenantId,
      entry_number: numData,
      entry_date: params.date,
      description: params.description,
      entry_type: params.entryType,
      source_type: params.sourceType,
      source_id: params.sourceId,
      created_by: params.createdBy,
      is_posted: true,
      idempotency_key: params.idempotencyKey || null,
    })
    .select('id')
    .single()

  if (error) {
    if (error.code === '23505' && params.idempotencyKey) {
      console.log('[CJE] Idempotency hit - journal already exists for key:', params.idempotencyKey)
      const { data: existing } = await db('journal_entries')
        .select('id')
        .eq('tenant_id', params.tenantId)
        .eq('idempotency_key', params.idempotencyKey)
        .not('is_voided', 'is', true)
        .single()
      return existing?.id ?? null
    }
    throw new Error(`Journal entry insert failed (${error.code}): ${error.message}`)
  }
  if (!entry) throw new Error('Journal entry insert returned no data')

  const lines = params.lines.map(l => ({
    tenant_id: params.tenantId,
    journal_id: entry.id,
    account_id: l.accountId,
    description: l.description,
    debit_amount: l.debit,
    credit_amount: l.credit,
    contact_type: l.contactType ?? null,
    contact_id: l.contactId ?? null,
    contact_name: l.contactName ?? null,
    payment_account_id: l.paymentAccountId ?? null,
  }))

  console.log('[CJE] Inserting lines:', JSON.stringify(lines))

  const { error: linesError } = await db('journal_entry_lines').insert(lines)

  if (linesError) {
    console.error('[CJE] Lines insert FAILED:', linesError)
    await db('journal_entries').delete().eq('id', entry.id)
    throw new Error('Failed to insert journal lines: ' + linesError.message)
  }

  console.log('[CJE] Lines inserted OK')

  return entry.id
}

export async function postGoodsInTransit(params: {
  tenantId: string
  purchaseId: string
  purchaseDate: string
  supplierId?: string
  supplierName: string
  totalAmount: number
  createdBy?: string
  idempotencyKey?: string
}): Promise<string | null> {
  const t = params.tenantId
  const safeName = params.supplierName?.trim() || 'Supplier'
  const transit = await getAccount(t, 'goods_in_transit')
  const payable = await getAccount(t, 'accounts_payable') || await getAccount(t, 'payable')
  if (!transit || !payable) {
    throw new Error('Goods in Transit (1202) or Accounts Payable account not found')
  }
  console.log('[GoodsInTransit] Posting:', params.totalAmount)
  return await createJournalEntry({
    tenantId: t,
    date: params.purchaseDate,
    description: `Goods in transit - ${safeName}`,
    entryType: 'purchase',
    sourceType: 'purchase_orders',
    sourceId: params.purchaseId,
    createdBy: params.createdBy,
    idempotencyKey: params.idempotencyKey,
    lines: [
      {
        accountId: transit,
        debit: params.totalAmount,
        credit: 0,
        description: 'Goods dispatched by supplier - in transit',
      },
      {
        accountId: payable,
        debit: 0,
        credit: params.totalAmount,
        description: `Payable to ${safeName}`,
        contactType: 'supplier',
        contactId: params.supplierId,
        contactName: safeName,
      },
    ],
  })
}

export async function postGoodsReceived(params: {
  tenantId: string
  receiptId: string
  receiptDate: string
  purchaseId: string
  supplierId?: string
  supplierName: string
  totalValue: number
  items: Array<{ productName: string; quantityReceived: number; unitCost: number }>
  createdBy?: string
  idempotencyKey?: string
}): Promise<string | null> {
  const t = params.tenantId
  const safeName = params.supplierName?.trim() || 'Supplier'
  const inventory = await getAccount(t, 'inventory')
  const transit = await getAccount(t, 'goods_in_transit')
  if (!inventory || !transit) {
    throw new Error('Stock on Hand (1201) or Goods in Transit (1202) not found')
  }
  const itemDesc = params.items.map(i => `${i.productName} x${i.quantityReceived}`).join(', ')
  return await createJournalEntry({
    tenantId: t,
    date: params.receiptDate,
    description: `Goods received from ${safeName}`,
    entryType: 'purchase',
    sourceType: 'purchase_receipts',
    sourceId: params.receiptId,
    createdBy: params.createdBy,
    idempotencyKey: params.idempotencyKey,
    lines: [
      {
        accountId: inventory,
        debit: params.totalValue,
        credit: 0,
        description: `Received: ${itemDesc}`,
      },
      {
        accountId: transit,
        debit: 0,
        credit: params.totalValue,
        description: `Cleared from transit - ${safeName}`,
      },
    ],
  })
}

export async function postCancelTransit(params: {
  tenantId: string
  purchaseId: string
  cancelDate: string
  supplierId?: string
  supplierName: string
  remainingTransitValue: number
  createdBy?: string
  idempotencyKey?: string
}): Promise<string | null> {
  const t = params.tenantId
  const safeName = params.supplierName?.trim() || 'Supplier'
  const transit = await getAccount(t, 'goods_in_transit')
  const payable = await getAccount(t, 'accounts_payable') || await getAccount(t, 'payable')
  if (!transit || !payable) {
    throw new Error('Required accounts not found')
  }
  return await createJournalEntry({
    tenantId: t,
    date: params.cancelDate,
    description: `Cancelled - goods in transit reversed - ${safeName}`,
    entryType: 'purchase',
    sourceType: 'purchase_orders',
    sourceId: params.purchaseId,
    createdBy: params.createdBy,
    idempotencyKey: params.idempotencyKey,
    lines: [
      {
        accountId: payable,
        debit: params.remainingTransitValue,
        credit: 0,
        description: `Reversal - cancelled PO ${safeName}`,
        contactType: 'supplier',
        contactId: params.supplierId,
        contactName: safeName,
      },
      {
        accountId: transit,
        debit: 0,
        credit: params.remainingTransitValue,
        description: 'Goods in transit reversed - PO cancelled',
      },
    ],
  })
}

// getCustomerAdvanceBalance (read the denormalized customers.outstanding_balance)
// was RETIRED: that column drifts and produced a phantom advance the server guard
// rejected. Available advance now comes from getAvailableCustomerAdvance
// (src/lib/contactBalances.ts), which mirrors the post_sale_atomic guard exactly.

export async function postSaleInvoice(params: {
  tenantId: string
  saleId: string
  saleDate: string
  customerId?: string
  customerName: string
  totalAmount: number
  vatAmount: number
  cogsAmount?: number
  paidAmount?: number
  paymentLines?: Array<{ accountId: string; amount: number }>
  advanceApplied?: number
  createdBy?: string
  idempotencyKey?: string
}): Promise<string | null> {
  const t = params.tenantId
  const cogsAmt = params.cogsAmount ?? 0
  const paidAmt = params.paidAmount ?? 0
  const pmtLines = params.paymentLines ?? []
  const advanceAmt = params.advanceApplied ?? 0

  const revenue = await getAccount(t, 'sales_revenue')
  if (!revenue) throw new Error('Sales revenue account not found — check account_mappings setup')

  const vatPayable = params.vatAmount > 0 ? await getAccount(t, 'vat_payable') : null
  const cogs = cogsAmt > 0 ? await getAccount(t, 'cost_of_goods_sold') : null
  const inventory = cogsAmt > 0 ? await getAccount(t, 'inventory') : null

  // Advance application now uses AR (negative AR is the advance)
  const arAccount = (advanceAmt > 0 || (params.totalAmount - paidAmt - advanceAmt) > 0.001)
    ? await getAccount(t, 'accounts_receivable') : null

  const unpaidBalance = params.totalAmount - paidAmt - advanceAmt
  const receivable = unpaidBalance > 0.001 ? arAccount : null

  const netRevenue = params.totalAmount - params.vatAmount

  const lines: JournalLine[] = []

  // Advance application: Dr AR (clears the customer's negative AR / advance)
  if (advanceAmt > 0 && arAccount) {
    lines.push({
      accountId: arAccount,
      description: `Advance applied — ${params.customerName}`,
      debit: advanceAmt,
      credit: 0,
      contactType: 'customer',
      contactId: params.customerId,
      contactName: params.customerName,
    })
  }

  // Cash payment lines (one per payment method)
  for (const pl of pmtLines) {
    if (pl.amount > 0) {
      lines.push({ accountId: pl.accountId, description: 'Payment received', debit: pl.amount, credit: 0 })
    }
  }

  // AR line for unpaid balance
  if (unpaidBalance > 0.001) {
    if (!receivable) {
      console.error('[postSaleInvoice] Receivable account not found for unpaid balance')
    } else {
      lines.push({
        accountId: receivable,
        description: `Sale to ${params.customerName}`,
        debit: unpaidBalance,
        credit: 0,
        contactType: 'customer',
        contactId: params.customerId,
        contactName: params.customerName,
      })
    }
  }

  // Revenue credit
  lines.push({
    accountId: revenue,
    description: 'Sales revenue',
    debit: 0,
    credit: netRevenue > 0 ? netRevenue : params.totalAmount,
  })

  // VAT credit
  if (params.vatAmount > 0 && vatPayable) {
    lines.push({ accountId: vatPayable, description: 'Output VAT payable', debit: 0, credit: params.vatAmount })
  }

  // COGS debit + Stock credit
  if (cogsAmt > 0 && cogs && inventory) {
    lines.push({ accountId: cogs, description: 'Cost of goods sold', debit: cogsAmt, credit: 0 })
    lines.push({ accountId: inventory, description: 'Stock on hand reduction', debit: 0, credit: cogsAmt })
  }

  if (lines.length < 2) throw new Error('postSaleInvoice: insufficient journal lines — likely a configuration error')

  return await createJournalEntry({
    tenantId: t,
    date: params.saleDate,
    description: `Sale invoice - ${params.customerName}`,
    entryType: 'sale',
    sourceType: 'sales_orders',
    sourceId: params.saleId,
    createdBy: params.createdBy,
    idempotencyKey: params.idempotencyKey,
    lines,
  })
}

export async function postSaleCOGS(params: {
  tenantId: string
  saleId: string
  saleDate: string
  cogsAmount: number
}) {
  const t = params.tenantId
  const cogs = await getAccount(t, 'cost_of_goods_sold')
  const inventory = await getAccount(t, 'inventory')
  if (!cogs || !inventory) return

  await createJournalEntry({
    tenantId: t,
    date: params.saleDate,
    description: 'Cost of goods sold',
    entryType: 'sale',
    sourceType: 'sales_orders',
    sourceId: params.saleId,
    lines: [
      { accountId: cogs, debit: params.cogsAmount, credit: 0 },
      { accountId: inventory, debit: 0, credit: params.cogsAmount },
    ],
  })
}

/**
 * @deprecated Build 3 (mig 231) moved after-sale customer receipts into the
 * atomic `post_customer_receipt_atomic` RPC. Call that RPC directly instead.
 * This thin wrapper is retained only for legacy callers: it posts ONLY the
 * receipt journal (DR payment account / CR Accounts Receivable, the deposit-only
 * shape — it creates NO order_payments rows and does NOT touch sales_orders).
 * Returns the journal id.
 */
export async function postPaymentReceived(params: {
  tenantId: string
  paymentId: string
  paymentDate: string
  customerId?: string
  customerName: string
  amount: number
  paymentMethod: string
  paymentAccountId?: string
  idempotencyKey?: string
}): Promise<string | null> {
  if (!params.paymentAccountId) return null

  const { data, error } = await rpc('post_customer_receipt_atomic', {
    p_tenant_id: params.tenantId,
    p_customer_id: params.customerId ?? null,
    p_payment_date: params.paymentDate,
    p_methods: [{
      payment_account_id: params.paymentAccountId,
      amount: params.amount,
      payment_method: params.paymentMethod,
    }],
    p_allocations: [],
    p_note: null,
    p_created_by: null,
    p_idempotency_key: params.idempotencyKey ?? null,
  })
  if (error) throw new Error(error.message)
  return (data?.journal_id as string) ?? null
}

/**
 * @deprecated Build 3 (mig 231) moved after-sale customer receipts into the
 * atomic `post_customer_receipt_atomic` RPC. Call that RPC directly instead.
 * Thin wrapper — posts ONLY the multi-method receipt journal (deposit-only
 * shape: no order_payments rows, no sales_orders update). Returns the journal id.
 */
export async function postMultiMethodPaymentReceived(params: {
  tenantId: string
  paymentId: string
  paymentDate: string
  customerId?: string
  customerName: string
  methods: Array<{ accountId: string; amount: number; paymentMethod: string }>
  createdBy?: string
}): Promise<string | null> {
  const { data, error } = await rpc('post_customer_receipt_atomic', {
    p_tenant_id: params.tenantId,
    p_customer_id: params.customerId ?? null,
    p_payment_date: params.paymentDate,
    p_methods: params.methods.map(m => ({
      payment_account_id: m.accountId,
      amount: m.amount,
      payment_method: m.paymentMethod,
    })),
    p_allocations: [],
    p_note: null,
    p_created_by: params.createdBy ?? null,
    p_idempotency_key: null,
  })
  if (error) throw new Error(error.message)
  return (data?.journal_id as string) ?? null
}

export async function postPurchaseInvoice(params: {
  tenantId: string
  purchaseId: string
  purchaseDate: string
  supplierId?: string
  supplierName: string
  totalAmount: number
  vatAmount: number
  createdBy?: string
  idempotencyKey?: string
}): Promise<string | null> {
  const t = params.tenantId

  // Always try 1201 Stock on Hand first
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: stockAccount } = await (supabase as any)
    .from('accounts')
    .select('id')
    .eq('tenant_id', t)
    .eq('code', '1201')
    .single() as { data: { id: string } | null }
  const inventory = stockAccount?.id ?? await getAccount(t, 'inventory')

  const payable = await getAccount(t, 'accounts_payable') || await getAccount(t, 'payable')
  const inputVat = await getAccount(t, 'input_vat_recoverable')

  console.log('[postPurchaseInvoice] Found accounts:', { inventory, payable, amount: params.totalAmount })

  if (!inventory) {
    throw new Error('Stock on Hand (1201) not found. Cannot post purchase journal.')
  }
  if (!payable) {
    throw new Error('Accounts Payable not found in Chart of Accounts. Please check account mappings.')
  }

  const lines: JournalLine[] = [
    {
      accountId: inventory,
      debit: params.totalAmount,
      credit: 0,
      description: 'Stock received',
      contactType: undefined,
      contactId: undefined,
      contactName: undefined,
    },
    {
      accountId: payable,
      debit: 0,
      credit: params.totalAmount,
      description: `Payable to ${params.supplierName}`,
      contactType: 'supplier',
      contactId: params.supplierId,
      contactName: params.supplierName,
    },
  ]

  if (params.vatAmount > 0 && inputVat) {
    lines[0].debit = params.totalAmount - params.vatAmount
    lines.splice(1, 0, { accountId: inputVat, debit: params.vatAmount, credit: 0, description: 'Input VAT recoverable' })
  }

  const journalId = await createJournalEntry({
    tenantId: t,
    date: params.purchaseDate,
    description: `Purchase from ${params.supplierName}`,
    entryType: 'purchase',
    sourceType: 'purchase_orders',
    sourceId: params.purchaseId,
    createdBy: params.createdBy,
    idempotencyKey: params.idempotencyKey,
    lines,
  })

  console.log('[postPurchaseInvoice] Posted:', journalId)
  return journalId
}

export async function postPurchasePayments(params: {
  tenantId: string
  purchaseId: string
  purchaseDate: string
  supplierId?: string
  supplierName: string
  totalPaid: number
  paymentLines: Array<{
    method: string
    accountId: string | null
    accountName?: string
    amount: number
  }>
  createdBy?: string
}): Promise<string | null> {
  console.log('[PPP] Called with params:', JSON.stringify({
    tenantId: params.tenantId,
    purchaseId: params.purchaseId,
    supplierId: params.supplierId,
    totalPaid: params.totalPaid,
    paymentLines: params.paymentLines,
  }))

  if (params.totalPaid <= 0) return null
  if (!params.paymentLines.length) return null

  const t = params.tenantId
  const payable = await getAccount(t, 'payable')

  console.log('[PPP] Payable account:', payable)

  if (!payable) return null

  // Validate all payment lines BEFORE creating journal header
  const validLines: JournalLine[] = []

  for (const payment of params.paymentLines) {
    if (!payment.accountId) {
      throw new Error(
        `Payment account not selected for amount ₦${payment.amount}. Please select an account.`
      )
    }
    if (payment.amount <= 0) continue
    validLines.push({
      accountId: payment.accountId,
      debit: 0,
      credit: payment.amount,
      description: `Payment via ${payment.accountName || 'account'}`,
    })
  }

  console.log('[PPP] Valid lines built:', JSON.stringify(validLines))

  if (validLines.length === 0) {
    throw new Error('No valid payment lines to post.')
  }

  // DR Accounts Payable + CR payment accounts
  const allLines: JournalLine[] = [
    {
      accountId: payable,
      debit: params.totalPaid,
      credit: 0,
      description: `Payment to ${params.supplierName}`,
      contactType: 'supplier',
      contactId: params.supplierId,
      contactName: params.supplierName,
    },
    ...validLines,
  ]

  console.log('[PPP] All lines:', JSON.stringify(allLines))

  // Validate balance before creating
  const totalDebit = allLines.reduce((s, l) => s + l.debit, 0)
  const totalCredit = allLines.reduce((s, l) => s + l.credit, 0)

  if (Math.abs(totalDebit - totalCredit) > 0.01) {
    throw new Error(`Payment journal unbalanced: DR ₦${totalDebit} CR ₦${totalCredit}`)
  }

  const journalId = await createJournalEntry({
    tenantId: t,
    date: params.purchaseDate,
    description: `Payment to ${params.supplierName}`,
    entryType: 'payment',
    sourceType: 'purchase_orders',
    sourceId: params.purchaseId,
    createdBy: params.createdBy,
    lines: allLines,
  })

  console.log('[PPP] Journal created:', journalId)

  return journalId
}

export async function postExpense(params: {
  tenantId: string
  expenseId: string
  expenseDate: string
  description: string
  amount: number
  whtAmount: number
  paymentMethod: string
  expenseAccountId?: string
  idempotencyKey?: string
}) {
  const t = params.tenantId
  const expenseAccount = params.expenseAccountId ?? (await getAccount(t, 'sundry_expense'))
  const paymentAccount = await getAccount(t, getPaymentMappingKey(params.paymentMethod))
  const whtPayable = await getAccount(t, 'wht_payable')
  if (!expenseAccount || !paymentAccount) return

  const netPaid = params.amount - params.whtAmount

  const lines: JournalLine[] = [
    { accountId: expenseAccount, debit: params.amount, credit: 0, description: params.description },
    { accountId: paymentAccount, debit: 0, credit: netPaid, description: 'Payment' },
  ]

  if (params.whtAmount > 0 && whtPayable) {
    lines.push({ accountId: whtPayable, debit: 0, credit: params.whtAmount, description: 'WHT withheld' })
  }

  await createJournalEntry({
    tenantId: t,
    date: params.expenseDate,
    description: params.description,
    entryType: 'expense',
    sourceType: 'expenses',
    sourceId: params.expenseId,
    idempotencyKey: params.idempotencyKey,
    lines,
  })
}

// Post accrual: DR expense account, CR accrued expenses liability
export async function postAccrualExpense(params: {
  tenantId: string
  expenseId: string
  expenseDate: string
  description: string
  amount: number
  supplierName?: string
  supplierId?: string
}) {
  const t = params.tenantId
  const expenseAccount = await getAccount(t, 'sundry_expense')
  const accruedAccount = await getAccount(t, 'accrued_expenses')
  if (!expenseAccount || !accruedAccount) return

  const lines: JournalLine[] = [
    { accountId: expenseAccount, debit: params.amount, credit: 0, description: params.description, contactType: params.supplierId ? 'supplier' : undefined, contactId: params.supplierId, contactName: params.supplierName },
    { accountId: accruedAccount, debit: 0, credit: params.amount, description: `Accrual — ${params.description}` },
  ]

  await createJournalEntry({
    tenantId: t,
    date: params.expenseDate,
    description: params.description,
    entryType: 'accrual',
    sourceType: 'expenses',
    sourceId: params.expenseId,
    lines,
  })
}

// Pay an accrual: DR accrued expenses, CR payment account
export async function postAccrualPayment(params: {
  tenantId: string
  expenseId: string
  paymentDate: string
  description: string
  amount: number
  paymentMethod: string
  paymentAccountId?: string
}) {
  const t = params.tenantId
  const accruedAccount = await getAccount(t, 'accrued_expenses')
  const paymentAccount = params.paymentAccountId
    ? params.paymentAccountId
    : await getAccount(t, getPaymentMappingKey(params.paymentMethod))
  if (!accruedAccount || !paymentAccount) return

  const lines: JournalLine[] = [
    { accountId: accruedAccount, debit: params.amount, credit: 0, description: `Pay accrual — ${params.description}` },
    { accountId: paymentAccount, debit: 0, credit: params.amount, description: 'Payment' },
  ]

  await createJournalEntry({
    tenantId: t,
    date: params.paymentDate,
    description: `Pay accrual — ${params.description}`,
    entryType: 'accrual_payment',
    sourceType: 'expenses',
    sourceId: params.expenseId,
    lines,
  })
}

export async function postPayroll(params: {
  tenantId: string
  paymentId: string
  paymentDate: string
  staffName: string
  grossSalary: number
  payeAmount: number
  pensionEmployee: number
  pensionEmployer: number
  nsitfAmount: number
  netSalary: number
}) {
  const t = params.tenantId
  const salaryExp = await getAccount(t, 'salaries_expense')
  const pensionExp = await getAccount(t, 'pension_expense')
  const payePayable = await getAccount(t, 'paye_payable')
  const pensionPayable = await getAccount(t, 'pension_payable')
  const salaryPayable = await getAccount(t, 'salary_payable')
  if (!salaryExp || !payePayable || !salaryPayable) return

  const lines: JournalLine[] = [
    { accountId: salaryExp, debit: params.grossSalary, credit: 0, description: `Salary - ${params.staffName}` },
    { accountId: payePayable, debit: 0, credit: params.payeAmount, description: 'PAYE deduction' },
  ]

  if (params.pensionEmployee > 0 && pensionPayable) {
    lines.push({ accountId: pensionPayable, debit: 0, credit: params.pensionEmployee, description: 'Pension deduction' })
  }

  lines.push({ accountId: salaryPayable, debit: 0, credit: params.netSalary, description: 'Net salary payable' })

  if (params.pensionEmployer > 0 && pensionExp && pensionPayable) {
    lines.push({ accountId: pensionExp, debit: params.pensionEmployer, credit: 0, description: 'Employer pension' })
    lines.push({ accountId: pensionPayable, debit: 0, credit: params.pensionEmployer, description: 'Employer pension payable' })
  }

  await createJournalEntry({
    tenantId: t,
    date: params.paymentDate,
    description: `Payroll - ${params.staffName}`,
    entryType: 'payroll',
    sourceType: 'salary_payments',
    sourceId: params.paymentId,
    lines,
  })
}

export async function postSchoolFeeInvoice(params: {
  tenantId: string
  paymentId: string
  paymentDate: string
  studentId?: string
  studentName: string
  amount: number
}) {
  const t = params.tenantId
  const receivable = await getAccount(t, 'school_receivable')
  const revenue = await getAccount(t, 'school_fee_revenue')
  if (!receivable || !revenue) return

  await createJournalEntry({
    tenantId: t,
    date: params.paymentDate,
    description: `School fee - ${params.studentName}`,
    entryType: 'school_fee',
    sourceType: 'student_fee_payments',
    sourceId: params.paymentId,
    lines: [
      {
        accountId: receivable,
        debit: params.amount,
        credit: 0,
        description: `Fee invoice - ${params.studentName}`,
        contactType: 'student',
        contactId: params.studentId,
        contactName: params.studentName,
      },
      { accountId: revenue, debit: 0, credit: params.amount },
    ],
  })
}

export async function postSchoolFeePayment(params: {
  tenantId: string
  paymentId: string
  paymentDate: string
  studentId?: string
  studentName: string
  amount: number
  paymentMethod: string
}) {
  const t = params.tenantId
  const paymentAccount = await getAccount(t, getPaymentMappingKey(params.paymentMethod))
  const receivable = await getAccount(t, 'school_receivable')
  if (!paymentAccount || !receivable) return

  await createJournalEntry({
    tenantId: t,
    date: params.paymentDate,
    description: `Fee payment - ${params.studentName}`,
    entryType: 'school_fee',
    sourceType: 'student_fee_payments',
    sourceId: params.paymentId,
    lines: [
      { accountId: paymentAccount, debit: params.amount, credit: 0 },
      {
        accountId: receivable,
        debit: 0,
        credit: params.amount,
        description: `Fee payment - ${params.studentName}`,
        contactType: 'student',
        contactId: params.studentId,
        contactName: params.studentName,
      },
    ],
  })
}

// ─── FIFO Payment Allocation ──────────────────────────────────────────────────

// ─── Journal Reversal ─────────────────────────────────────────────────────────

export async function reverseJournalEntry(params: {
  tenantId: string
  originalEntryId: string
  originalEntryNumber: string
  createdBy?: string
  reversalDate: string
}): Promise<{ newEntryId: string; newEntryNumber: string } | null> {
  // Fetch original entry lines
  const { data: lines, error } = await db('journal_entry_lines')
    .select('account_id, description, debit_amount, credit_amount, contact_type, contact_id, contact_name, payment_account_id')
    .eq('journal_id', params.originalEntryId)

  if (error || !lines?.length) return null

  // Create reversed entry (swap debits/credits)
  const { data: numData, error: numErr } = await rpc('next_journal_number', { p_tenant_id: params.tenantId })
  if (numErr || numData == null) throw new Error(`Journal number generation failed: ${numErr?.message ?? 'RPC returned null'}`)

  const { data: entry, error: entryError } = await db('journal_entries')
    .insert({
      tenant_id: params.tenantId,
      entry_number: numData,
      entry_date: params.reversalDate,
      description: `Reversal of ${params.originalEntryNumber}`,
      entry_type: 'manual',
      created_by: params.createdBy,
      is_posted: true,
    })
    .select('id, entry_number')
    .single()

  if (entryError) throw new Error(`Reversal entry insert failed (${entryError.code}): ${entryError.message}`)
  if (!entry) throw new Error('Reversal entry insert returned no data')

  const reversedLines = lines.map((l: {
    account_id: string
    description: string | null
    debit_amount: number
    credit_amount: number
    contact_type: string | null
    contact_id: string | null
    contact_name: string | null
    payment_account_id: string | null
  }) => ({
    tenant_id: params.tenantId,
    journal_id: entry.id,
    account_id: l.account_id,
    description: l.description,
    debit_amount: l.credit_amount,
    credit_amount: l.debit_amount,
    contact_type: l.contact_type,
    contact_id: l.contact_id,
    contact_name: l.contact_name,
    payment_account_id: l.payment_account_id,
  }))

  await db('journal_entry_lines').insert(reversedLines)

  // Mark original as reversed
  await db('journal_entries')
    .update({ is_reversed: true, reversed_by: entry.id })
    .eq('id', params.originalEntryId)

  return { newEntryId: entry.id, newEntryNumber: entry.entry_number }
}

// ─── Opening Balances ─────────────────────────────────────────────────────────

export async function postOpeningBalances(params: {
  tenantId: string
  openingId: string
  asAtDate: string
  createdBy?: string
  lines: Array<{
    accountId: string
    description: string
    amount: number
    entrySide: 'debit' | 'credit'
    contactType?: string
    contactId?: string
    contactName?: string
  }>
}): Promise<string | null> {
  const journalLines: JournalLine[] = params.lines.map(l => ({
    accountId: l.accountId,
    description: l.description,
    debit: l.entrySide === 'debit' ? l.amount : 0,
    credit: l.entrySide === 'credit' ? l.amount : 0,
    contactType: l.contactType,
    contactId: l.contactId,
    contactName: l.contactName,
  }))

  const journalId = await createJournalEntry({
    tenantId: params.tenantId,
    date: params.asAtDate,
    description: 'Opening balances',
    entryType: 'opening_balance',
    createdBy: params.createdBy,
    lines: journalLines,
  })

  if (!journalId) return null

  const { error: updateError } = await db('opening_balances')
    .update({
      journal_id: journalId,
      is_posted: true,
      posted_at: new Date().toISOString(),
      posted_by: params.createdBy,
    })
    .eq('id', params.openingId)

  if (updateError) {
    await rollbackJournal(journalId, params.tenantId)
    throw new Error('Failed to update opening balance record: ' + updateError.message)
  }

  return journalId
}

// ─── Stock Adjustment ─────────────────────────────────────────────────────────

export async function postStockAdjustment(params: {
  tenantId: string
  entryId: string
  adjustmentDate: string
  productName: string
  quantity: number
  costPrice: number
  adjustmentType: 'increase' | 'decrease'
}) {
  const value = Math.abs(params.quantity) * params.costPrice
  if (value === 0) return

  const t = params.tenantId
  const inventory = await getAccount(t, 'inventory')
  const writeoff = await getAccount(t, 'stock_writeoff') ?? await getAccount(t, 'sundry_expense')
  if (!inventory || !writeoff) return

  const isIncrease = params.adjustmentType === 'increase'

  await createJournalEntry({
    tenantId: t,
    date: params.adjustmentDate,
    description: `Stock adjustment - ${params.productName}`,
    entryType: 'stock',
    sourceType: 'stock_entries',
    sourceId: params.entryId,
    lines: [
      {
        accountId: isIncrease ? inventory : writeoff,
        debit: value,
        credit: 0,
        description: isIncrease ? `${params.productName} stock increase` : `${params.productName} stock decrease`,
      },
      {
        accountId: isIncrease ? writeoff : inventory,
        debit: 0,
        credit: value,
        description: isIncrease ? 'Stock adjustment account' : 'Inventory reduction',
      },
    ],
  })
}

// ─── Tax Remittance ───────────────────────────────────────────────────────────

export async function postTaxRemittance(params: {
  tenantId: string
  taxType: 'vat' | 'paye' | 'wht' | 'pension' | 'nhf' | 'nsitf'
  amount: number
  paymentDate: string
  reference: string
  paymentAccountId?: string
}): Promise<string | null> {
  const t = params.tenantId
  const taxAccountMap: Record<string, string> = {
    vat: 'vat_payable',
    paye: 'paye_payable',
    wht: 'wht_payable',
    pension: 'pension_payable',
    nhf: 'nhf_payable',
    nsitf: 'nsitf_payable',
  }
  const taxNames: Record<string, string> = {
    vat: 'VAT Remittance to FIRS',
    paye: 'PAYE Remittance to State IRS',
    wht: 'WHT Remittance to FIRS',
    pension: 'Pension Remittance to PFA',
    nhf: 'NHF Remittance to FMBN',
    nsitf: 'NSITF Remittance',
  }

  const taxAccount = await getAccount(t, taxAccountMap[params.taxType])
  const paymentAccount = params.paymentAccountId ?? (await getAccount(t, 'bank'))
  if (!taxAccount || !paymentAccount) return null

  return await createJournalEntry({
    tenantId: t,
    date: params.paymentDate,
    description: taxNames[params.taxType],
    entryType: 'tax',
    lines: [
      {
        accountId: taxAccount,
        debit: params.amount,
        credit: 0,
        description: taxNames[params.taxType],
      },
      {
        accountId: paymentAccount,
        debit: 0,
        credit: params.amount,
        description: `Payment ref: ${params.reference}`,
      },
    ],
  })
}

// ─── Loan Disbursement ────────────────────────────────────────────────────────

export async function postLoanDisbursement(params: {
  tenantId: string
  loanId: string
  disbursementDate: string
  lenderName: string
  amount: number
  disbursementAccountCoaId: string
  loanPayableAccountId?: string
}) {
  const t = params.tenantId
  const loanPayable = params.loanPayableAccountId ?? (await getAccount(t, 'loan_payable'))
  if (!loanPayable || !params.disbursementAccountCoaId) return

  await createJournalEntry({
    tenantId: t,
    date: params.disbursementDate,
    description: `Loan disbursement from ${params.lenderName}`,
    entryType: 'loan',
    sourceType: 'loans',
    sourceId: params.loanId,
    lines: [
      {
        accountId: params.disbursementAccountCoaId,
        debit: params.amount,
        credit: 0,
        description: `Loan from ${params.lenderName}`,
      },
      {
        accountId: loanPayable,
        debit: 0,
        credit: params.amount,
        description: `Loan payable - ${params.lenderName}`,
        contactName: params.lenderName,
      },
    ],
  })
}

// ─── Loan Payment ─────────────────────────────────────────────────────────────

export async function postLoanPayment(params: {
  tenantId: string
  scheduleId: string
  paymentDate: string
  lenderName: string
  principalAmount: number
  interestAmount: number
  paymentAccountCoaId: string
  loanPayableAccountId?: string
  interestExpenseAccountId?: string
}) {
  const t = params.tenantId
  const loanPayable = params.loanPayableAccountId ?? (await getAccount(t, 'loan_payable'))
  const interestExpense = params.interestExpenseAccountId ?? (await getAccount(t, 'interest_expense'))
  if (!loanPayable || !params.paymentAccountCoaId) return

  const totalPayment = params.principalAmount + params.interestAmount

  const lines: JournalLine[] = [
    {
      accountId: loanPayable,
      debit: params.principalAmount,
      credit: 0,
      description: `Principal repayment - ${params.lenderName}`,
      contactName: params.lenderName,
    },
    {
      accountId: params.paymentAccountCoaId,
      debit: 0,
      credit: totalPayment,
      description: `Loan payment to ${params.lenderName}`,
    },
  ]

  if (params.interestAmount > 0 && interestExpense) {
    lines.splice(1, 0, {
      accountId: interestExpense,
      debit: params.interestAmount,
      credit: 0,
      description: 'Interest expense',
    })
  }

  await createJournalEntry({
    tenantId: t,
    date: params.paymentDate,
    description: `Loan repayment - ${params.lenderName}`,
    entryType: 'loan',
    sourceType: 'loan_schedules',
    sourceId: params.scheduleId,
    lines,
  })
}

// ─── Bad Debt Write-off ───────────────────────────────────────────────────────

export async function postBadDebtWriteoff(params: {
  tenantId: string
  sourceId: string
  writeoffDate: string
  customerName: string
  amount: number
  useProvision?: boolean
  createdBy?: string
}) {
  const t = params.tenantId
  const receivable = await getAccount(t, 'accounts_receivable')
  const badDebtExpense = await getAccount(t, 'bad_debt_expense')
  const badDebtProvision = await getAccount(t, 'bad_debt_provision')
  if (!receivable) return

  if (params.useProvision && badDebtProvision) {
    await createJournalEntry({
      tenantId: t,
      date: params.writeoffDate,
      description: `Bad debt written off - ${params.customerName}`,
      entryType: 'bad_debt',
      sourceType: 'sales_orders',
      sourceId: params.sourceId,
      createdBy: params.createdBy,
      lines: [
        {
          accountId: badDebtProvision,
          debit: params.amount,
          credit: 0,
          description: 'Utilise provision for bad debt',
        },
        {
          accountId: receivable,
          debit: 0,
          credit: params.amount,
          description: `Write off - ${params.customerName}`,
          contactType: 'customer',
          contactName: params.customerName,
        },
      ],
    })
  } else if (badDebtExpense) {
    await createJournalEntry({
      tenantId: t,
      date: params.writeoffDate,
      description: `Bad debt written off - ${params.customerName}`,
      entryType: 'bad_debt',
      sourceType: 'sales_orders',
      sourceId: params.sourceId,
      createdBy: params.createdBy,
      lines: [
        {
          accountId: badDebtExpense,
          debit: params.amount,
          credit: 0,
          description: `Bad debt expense - ${params.customerName}`,
        },
        {
          accountId: receivable,
          debit: 0,
          credit: params.amount,
          description: `Write off - ${params.customerName}`,
          contactType: 'customer',
          contactName: params.customerName,
        },
      ],
    })
  }
}

// ─── Bad Debt Recovery ────────────────────────────────────────────────────────

export async function postBadDebtRecovery(params: {
  tenantId: string
  sourceId: string
  recoveryDate: string
  customerName: string
  amount: number
  paymentAccountCoaId: string
  createdBy?: string
}) {
  const t = params.tenantId
  const badDebtExpense = await getAccount(t, 'bad_debt_expense')
  if (!badDebtExpense || !params.paymentAccountCoaId) return

  await createJournalEntry({
    tenantId: t,
    date: params.recoveryDate,
    description: `Bad debt recovery - ${params.customerName}`,
    entryType: 'bad_debt',
    sourceType: 'sales_orders',
    sourceId: params.sourceId,
    createdBy: params.createdBy,
    lines: [
      {
        accountId: params.paymentAccountCoaId,
        debit: params.amount,
        credit: 0,
        description: `Recovery from ${params.customerName}`,
      },
      {
        accountId: badDebtExpense,
        debit: 0,
        credit: params.amount,
        description: 'Bad debt recovered',
      },
    ],
  })
}

// ─── Prepayment / Accrual Schedule ───────────────────────────────────────────

export async function postPrepaymentExpense(params: {
  tenantId: string
  expenseId: string
  expenseDate: string
  description: string
  totalAmount: number
  paymentMethod: string
  expenseAccountId?: string
}) {
  const t = params.tenantId
  const prepaidAccount = await getAccount(t, 'prepaid_expenses')
  const paymentAccount = await getAccount(t, getPaymentMappingKey(params.paymentMethod))
  if (!prepaidAccount || !paymentAccount) {
    // Fallback to normal expense post
    return postExpense({
      tenantId: t,
      expenseId: params.expenseId,
      expenseDate: params.expenseDate,
      description: params.description,
      amount: params.totalAmount,
      whtAmount: 0,
      paymentMethod: params.paymentMethod,
      expenseAccountId: params.expenseAccountId,
    })
  }

  await createJournalEntry({
    tenantId: t,
    date: params.expenseDate,
    description: `Prepayment - ${params.description}`,
    entryType: 'expense',
    sourceType: 'expenses',
    sourceId: params.expenseId,
    lines: [
      {
        accountId: prepaidAccount,
        debit: params.totalAmount,
        credit: 0,
        description: `Prepaid - ${params.description}`,
      },
      {
        accountId: paymentAccount,
        debit: 0,
        credit: params.totalAmount,
        description: 'Payment',
      },
    ],
  })
}

export async function postScheduledAmortisation(params: {
  tenantId: string
  scheduleLineId: string
  monthDate: string
  description: string
  amount: number
  expenseAccountId?: string
  assetAccountId?: string
  entryType: 'prepayment' | 'accrual'
}) {
  const t = params.tenantId
  const expenseAccount = params.expenseAccountId ?? (await getAccount(t, 'sundry_expense'))
  const assetOrLiabilityAccount = params.assetAccountId ?? (await getAccount(t, params.entryType === 'prepayment' ? 'prepaid_expenses' : 'accrued_liabilities'))
  if (!expenseAccount || !assetOrLiabilityAccount) return

  const lines: JournalLine[] = params.entryType === 'prepayment'
    ? [
      { accountId: expenseAccount, debit: params.amount, credit: 0, description: params.description },
      { accountId: assetOrLiabilityAccount, debit: 0, credit: params.amount, description: 'Prepaid release' },
    ]
    : [
      { accountId: expenseAccount, debit: params.amount, credit: 0, description: params.description },
      { accountId: assetOrLiabilityAccount, debit: 0, credit: params.amount, description: 'Accrual' },
    ]

  await createJournalEntry({
    tenantId: t,
    date: params.monthDate,
    description: params.description,
    entryType: 'expense',
    sourceType: 'expense_schedules',
    sourceId: params.scheduleLineId,
    lines,
  })
}

// ─── Customer Deposit ─────────────────────────────────────────────────────────

export async function postCustomerDeposit(params: {
  tenantId: string
  depositId: string
  depositDate: string
  customerId?: string
  customerName: string
  amount: number
  paymentAccountCoaId: string
  createdBy?: string
}) {
  const t = params.tenantId
  const receivable = await getAccount(t, 'accounts_receivable')
  if (!receivable || !params.paymentAccountCoaId) return

  await createJournalEntry({
    tenantId: t,
    date: params.depositDate,
    description: `Customer advance - ${params.customerName}`,
    entryType: 'payment',
    sourceType: 'order_payments',
    sourceId: params.depositId,
    createdBy: params.createdBy,
    lines: [
      { accountId: params.paymentAccountCoaId, debit: params.amount, credit: 0, description: 'Advance payment received' },
      { accountId: receivable, debit: 0, credit: params.amount, description: `Customer advance - ${params.customerName}`, contactType: 'customer', contactId: params.customerId, contactName: params.customerName },
    ],
  })
}

// ─── Petty Cash Top-Up ────────────────────────────────────────────────────────

export async function postPettyCashTopUp(params: {
  tenantId: string
  pettyCashId: string
  pettyCashName: string
  amount: number
  paymentDate: string
  paymentAccountCoaId: string
  createdBy?: string
}) {
  const t = params.tenantId
  const pettyCashAccount = await getAccount(t, 'petty_cash')
  if (!pettyCashAccount || !params.paymentAccountCoaId) return

  await createJournalEntry({
    tenantId: t,
    date: params.paymentDate,
    description: `Petty cash top-up - ${params.pettyCashName}`,
    entryType: 'manual',
    sourceType: 'petty_cash_transactions',
    sourceId: params.pettyCashId,
    createdBy: params.createdBy,
    lines: [
      { accountId: pettyCashAccount, debit: params.amount, credit: 0, description: params.pettyCashName },
      { accountId: params.paymentAccountCoaId, debit: 0, credit: params.amount, description: 'Transfer to petty cash' },
    ],
  })
}

// ─── Petty Cash Expense ───────────────────────────────────────────────────────

export async function postPettyCashExpense(params: {
  tenantId: string
  pettyCashId: string
  pettyCashName: string
  amount: number
  description: string
  expenseDate: string
  expenseAccountId?: string
  createdBy?: string
}) {
  const t = params.tenantId
  const pettyCashAccount = await getAccount(t, 'petty_cash')
  const expenseAccount = params.expenseAccountId ?? (await getAccount(t, 'sundry_expense'))
  if (!pettyCashAccount || !expenseAccount) return

  await createJournalEntry({
    tenantId: t,
    date: params.expenseDate,
    description: params.description,
    entryType: 'expense',
    sourceType: 'petty_cash_transactions',
    sourceId: params.pettyCashId,
    createdBy: params.createdBy,
    lines: [
      { accountId: expenseAccount, debit: params.amount, credit: 0, description: params.description },
      { accountId: pettyCashAccount, debit: 0, credit: params.amount, description: `Petty cash - ${params.pettyCashName}` },
    ],
  })
}

// ─── Year-End Close ───────────────────────────────────────────────────────────

export async function postYearEndClose(params: {
  tenantId: string
  yearId: string
  yearName: string
  endDate: string
  netProfit: number
  createdBy?: string
}) {
  const t = params.tenantId
  const retainedEarnings = await getAccount(t, 'retained_earnings')
  const incomeSummary = await getAccount(t, 'income_summary') ?? retainedEarnings
  if (!retainedEarnings || !incomeSummary) return

  if (Math.abs(params.netProfit) < 0.01) return

  await createJournalEntry({
    tenantId: t,
    date: params.endDate,
    description: `Year-end close - ${params.yearName}`,
    entryType: 'manual',
    sourceType: 'financial_years',
    sourceId: params.yearId,
    createdBy: params.createdBy,
    lines: params.netProfit >= 0
      ? [
        { accountId: incomeSummary, debit: params.netProfit, credit: 0, description: 'Transfer profit to retained earnings' },
        { accountId: retainedEarnings, debit: 0, credit: params.netProfit, description: `Net profit ${params.yearName}` },
      ]
      : [
        { accountId: retainedEarnings, debit: Math.abs(params.netProfit), credit: 0, description: `Net loss ${params.yearName}` },
        { accountId: incomeSummary, debit: 0, credit: Math.abs(params.netProfit), description: 'Transfer loss to retained earnings' },
      ],
  })
}

// ─── Depreciation ─────────────────────────────────────────────────────────────

export async function postDepreciation(params: {
  tenantId: string
  assetId: string
  assetName: string
  amount: number
  month: string
  expenseAccountId: string
  accumDeprAccountId: string
}) {
  if (params.amount <= 0) return

  await createJournalEntry({
    tenantId: params.tenantId,
    date: params.month + '-01',
    description: `Depreciation - ${params.assetName}`,
    entryType: 'manual',
    sourceType: 'business_assets',
    sourceId: params.assetId,
    lines: [
      {
        accountId: params.expenseAccountId,
        debit: params.amount,
        credit: 0,
        description: 'Monthly depreciation',
      },
      {
        accountId: params.accumDeprAccountId,
        debit: 0,
        credit: params.amount,
        description: 'Accumulated depreciation',
      },
    ],
  })
}

// ─── Opening Stock Journal ────────────────────────────────────────────────────

export async function postOpeningStockJournal(params: {
  tenantId: string
  productId: string
  productName: string
  quantity: number
  costPrice: number
  totalValue: number
  importDate: string
  createdBy?: string
}): Promise<string | null> {
  const t = params.tenantId

  if (params.totalValue <= 0) {
    console.log('[OpeningStock] Skipping zero value:', params.productName)
    return null
  }

  // Try 1201 Stock on Hand directly first, fall back to mapping
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: stockAccount } = await (supabase as any)
    .from('accounts')
    .select('id, code, name')
    .eq('tenant_id', t)
    .eq('code', '1201')
    .single() as { data: { id: string; code: string; name: string } | null }

  let inventoryAccountId: string | null = stockAccount?.id ?? null
  if (stockAccount) {
    console.log('[OpeningStock] Using 1201:', stockAccount.name)
  } else {
    inventoryAccountId = await getAccount(t, 'inventory')
  }

  const openingEquity = await getAccount(t, 'opening_balance_equity')

  if (!inventoryAccountId) {
    throw new Error('Stock on Hand account (1201) not found.')
  }
  if (!openingEquity) {
    throw new Error('Opening Balance Equity (3003) account not found — contact support.')
  }

  console.log('[OpeningStock] Posting:', params.productName, '₦' + params.totalValue, '→ account', inventoryAccountId)

  return await createJournalEntry({
    tenantId: t,
    date: params.importDate,
    description: `Opening stock - ${params.productName}`,
    entryType: 'opening_balance',
    sourceType: 'products',
    sourceId: params.productId,
    createdBy: params.createdBy,
    lines: [
      {
        accountId: inventoryAccountId,
        debit: params.totalValue,
        credit: 0,
        description: `Opening stock: ${params.productName} (${params.quantity} units @ ₦${params.costPrice})`,
      },
      {
        accountId: openingEquity,
        debit: 0,
        credit: params.totalValue,
        description: 'Opening balance equity',
      },
    ],
  })
}

// ─── Purchase Return ──────────────────────────────────────────────────────────

export async function postPurchaseReturn(params: {
  tenantId: string
  returnId: string
  returnDate: string
  debitNoteNumber: string
  supplierId?: string
  supplierName: string
  totalAmount: number
  returnType: 'credit_note' | 'refund_cash' | 'supplier_prepayment'
  /** COA account id for cash refund — the account we receive money into */
  refundAccountId?: string
  createdBy?: string
  idempotencyKey?: string
}): Promise<string | null> {
  const t = params.tenantId

  // Try 1201 Stock on Hand directly first, fall back to inventory mapping
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: stockAccount } = await (supabase as any)
    .from('accounts')
    .select('id')
    .eq('tenant_id', t)
    .eq('code', '1201')
    .single() as { data: { id: string } | null }
  const inventory = stockAccount?.id ?? await getAccount(t, 'inventory')

  const payable = await getAccount(t, 'accounts_payable') || await getAccount(t, 'payable')
  const supplierPrepayments = await getAccount(t, 'supplier_prepayments')

  console.log('[PurchaseReturn] accounts:', { payable, inventory, amount: params.totalAmount })

  if (!inventory) {
    throw new Error(`Missing accounts: inventory=${inventory}`)
  }
  if (!payable && params.returnType === 'credit_note') {
    throw new Error(`Missing accounts: payable=${payable}`)
  }

  let lines: JournalLine[] = []

  if (params.returnType === 'credit_note') {
    if (!payable) return null
    lines = [
      {
        accountId: payable,
        debit: params.totalAmount,
        credit: 0,
        description: `Return to ${params.supplierName} - ${params.debitNoteNumber}`,
        contactType: 'supplier',
        contactId: params.supplierId,
        contactName: params.supplierName,
      },
      {
        accountId: inventory,
        debit: 0,
        credit: params.totalAmount,
        description: 'Stock returned to supplier',
      },
    ]
  } else if (params.returnType === 'refund_cash') {
    const refundAcct = params.refundAccountId
    if (!refundAcct) return null
    lines = [
      {
        accountId: refundAcct,
        debit: params.totalAmount,
        credit: 0,
        description: `Cash refund from ${params.supplierName}`,
      },
      {
        accountId: inventory,
        debit: 0,
        credit: params.totalAmount,
        description: 'Stock returned to supplier',
      },
    ]
  } else if (params.returnType === 'supplier_prepayment') {
    if (!supplierPrepayments) return null
    lines = [
      {
        accountId: supplierPrepayments,
        debit: params.totalAmount,
        credit: 0,
        description: `Credit from ${params.supplierName} - ${params.debitNoteNumber}`,
        contactType: 'supplier',
        contactId: params.supplierId,
        contactName: params.supplierName,
      },
      {
        accountId: inventory,
        debit: 0,
        credit: params.totalAmount,
        description: 'Stock returned to supplier',
      },
    ]
  }

  if (lines.length < 2) return null

  const journalId = await createJournalEntry({
    tenantId: t,
    date: params.returnDate,
    description: `Purchase Return ${params.debitNoteNumber} - ${params.supplierName}`,
    entryType: 'purchase_return',
    sourceType: 'purchase_returns',
    sourceId: params.returnId,
    createdBy: params.createdBy,
    idempotencyKey: params.idempotencyKey,
    lines,
  })

  return journalId
}

// ─── Supplier Refund ──────────────────────────────────────────────────────────

export async function postSupplierRefund(params: {
  tenantId: string
  refundId: string
  refundDate: string
  supplierId?: string
  supplierName: string
  amount: number
  paymentAccountId: string
  createdBy?: string
}): Promise<string | null> {
  const t = params.tenantId
  const payable = await getAccount(t, 'accounts_payable') || await getAccount(t, 'payable')
  const paymentAccount = params.paymentAccountId

  if (!payable || !paymentAccount) {
    throw new Error('Required accounts not found')
  }

  return await createJournalEntry({
    tenantId: t,
    date: params.refundDate,
    description: `Supplier refund - ${params.supplierName}`,
    entryType: 'payment',
    sourceType: 'refunds',
    sourceId: params.refundId,
    idempotencyKey: `${t}-refunds-${params.refundId}-supplier_refund`,
    createdBy: params.createdBy,
    lines: [
      {
        accountId: paymentAccount,
        debit: params.amount,
        credit: 0,
        description: `Refund received from ${params.supplierName}`,
      },
      {
        accountId: payable,
        debit: 0,
        credit: params.amount,
        description: `Supplier refund cleared - ${params.supplierName}`,
        contactType: 'supplier',
        contactId: params.supplierId,
        contactName: params.supplierName,
      },
    ],
  })
}

// ─── Sale Return ──────────────────────────────────────────────────────────────

type SaleReturnLinesParams = {
  salesReturnsAccountId: string
  counterAccountId: string
  inventoryAccountId: string | null
  cogsAccountId: string | null
  writeoffAccountId: string | null
  vatPayableAccountId?: string | null
  totalAmount: number
  vatAmount?: number
  restockCostAmount: number
  damagedCostAmount: number
  customerId?: string
  customerName?: string
}

export function buildSaleReturnLines(p: SaleReturnLinesParams): JournalLine[] {
  // Only split VAT if we have both a VAT amount and the VAT account — otherwise use gross on Sales Returns
  const hasVatSplit = !!(p.vatAmount && p.vatAmount > 0.001 && p.vatPayableAccountId)
  const vat = hasVatSplit ? p.vatAmount! : 0
  const netAmount = vat > 0 ? p.totalAmount - vat : p.totalAmount

  const lines: JournalLine[] = [
    {
      accountId: p.salesReturnsAccountId,
      description: 'Sales return',
      debit: netAmount,
      credit: 0,
    },
  ]

  if (hasVatSplit && p.vatPayableAccountId) {
    lines.push({ accountId: p.vatPayableAccountId, description: 'VAT reversed on return', debit: vat, credit: 0 })
  }

  lines.push({
    accountId: p.counterAccountId,
    description: p.customerName ? `Return to ${p.customerName}` : 'Return credit',
    debit: 0,
    credit: p.totalAmount,
    contactType: 'customer',
    contactId: p.customerId,
    contactName: p.customerName,
  })

  if (p.restockCostAmount > 0 && p.inventoryAccountId && p.cogsAccountId) {
    lines.push(
      { accountId: p.inventoryAccountId, description: 'Stock returned to inventory', debit: p.restockCostAmount, credit: 0 },
      { accountId: p.cogsAccountId, description: 'COGS reversed on return', debit: 0, credit: p.restockCostAmount },
    )
  }

  if (p.damagedCostAmount > 0 && p.writeoffAccountId && p.cogsAccountId) {
    lines.push(
      { accountId: p.writeoffAccountId, description: 'Damaged/expired goods written off', debit: p.damagedCostAmount, credit: 0 },
      { accountId: p.cogsAccountId, description: 'COGS reversed on damaged return', debit: 0, credit: p.damagedCostAmount },
    )
  }

  return lines
}

export async function postSaleReturn(params: {
  tenantId: string
  returnId: string
  returnDate: string
  returnNumber: string
  customerId?: string
  customerName: string
  returnType: 'refund' | 'customer_account'
  totalAmount: number
  vatAmount?: number
  restockCostAmount: number
  damagedCostAmount: number
  refundAccountId?: string
  createdBy?: string
  idempotencyKey?: string
}): Promise<string | null> {
  const t = params.tenantId

  const salesReturns = await getAccount(t, 'sales_returns') || await getAccount(t, 'sales_revenue')
  const receivable = await getAccount(t, 'accounts_receivable')
  const inventory = await getAccount(t, 'inventory')
  const cogs = await getAccount(t, 'cost_of_goods_sold')
  const writeoff = await getAccount(t, 'stock_writeoff') ?? await getAccount(t, 'sundry_expense')
  const vatPayable = params.vatAmount && params.vatAmount > 0 ? await getAccount(t, 'vat_payable') : null

  if (!salesReturns) {
    throw new Error('No sales account found (tried: sales_returns, sales_revenue). Set up account mappings in Chart of Accounts.')
  }

  let counterAccountId: string | null = null
  if (params.returnType === 'refund') {
    counterAccountId = params.refundAccountId ?? null
    if (!counterAccountId) throw new Error('Refund account required for cash refund')
  } else {
    counterAccountId = receivable
    if (!counterAccountId) throw new Error('Accounts Receivable account not found')
  }

  const lines = buildSaleReturnLines({
    salesReturnsAccountId: salesReturns,
    counterAccountId,
    inventoryAccountId: inventory,
    cogsAccountId: cogs,
    writeoffAccountId: writeoff,
    vatPayableAccountId: vatPayable,
    totalAmount: params.totalAmount,
    vatAmount: params.vatAmount,
    restockCostAmount: params.restockCostAmount,
    damagedCostAmount: params.damagedCostAmount,
    customerId: params.customerId,
    customerName: params.customerName,
  })

  return await createJournalEntry({
    tenantId: t,
    date: params.returnDate,
    description: `Sales Return ${params.returnNumber} - ${params.customerName}`,
    entryType: 'sale_return',
    sourceType: 'sale_returns',
    sourceId: params.returnId,
    createdBy: params.createdBy,
    idempotencyKey: params.idempotencyKey ?? `sale_return-${params.returnId}`,
    lines,
  })
}

// ─── Customer Refund ──────────────────────────────────────────────────────────

export async function postCustomerRefund(params: {
  tenantId: string
  refundId: string
  refundDate: string
  customerId?: string
  customerName: string
  amount: number
  paymentAccountId: string
  createdBy?: string
}): Promise<string | null> {
  const t = params.tenantId
  const receivable = await getAccount(t, 'accounts_receivable')
  const paymentAccount = params.paymentAccountId

  if (!receivable || !paymentAccount) {
    throw new Error('Required accounts not found')
  }

  return await createJournalEntry({
    tenantId: t,
    date: params.refundDate,
    description: `Customer refund - ${params.customerName}`,
    entryType: 'payment',
    sourceType: 'refunds',
    sourceId: params.refundId,
    idempotencyKey: `${t}-refunds-${params.refundId}-customer_refund`,
    createdBy: params.createdBy,
    lines: [
      {
        accountId: receivable,
        debit: params.amount,
        credit: 0,
        description: `Refund to ${params.customerName} - AR cleared`,
        contactType: 'customer',
        contactId: params.customerId,
        contactName: params.customerName,
      },
      {
        accountId: paymentAccount,
        debit: 0,
        credit: params.amount,
        description: `Refund paid to ${params.customerName}`,
      },
    ],
  })
}

async function assertPeriodOpen(tenantId: string, entryDate: string): Promise<void> {
  const d = new Date(entryDate + 'T00:00:00')
  const year = d.getFullYear()
  const month = d.getMonth() + 1
  const monthLabel = d.toLocaleString('en-US', { month: 'long' })
  const { data } = await db('locked_periods')
    .select('locked_at')
    .eq('tenant_id', tenantId)
    .eq('period_year', year)
    .eq('period_month', month)
    .maybeSingle()
  if (data) {
    throw new Error(
      `Cannot post to ${monthLabel} ${year} — period is locked. ` +
      `Unlock the period in Settings › Period Locks or process this as a manual journal in the current period.`
    )
  }
}

// Cancel a finalized sale. cancel_sale_atomic (migration 220) now performs the
// stock restore AND the GL reversal in a single atomic transaction, so the only
// remaining work here is an early period-lock pre-check (for a clean message)
// and reading the reversal-journal info back off the RPC return.
export async function cancelSaleWithReversal(params: {
  tenantId: string
  saleId: string
  userId?: string
  reason: string
  idempotencyKey?: string
}): Promise<{ cancelled: boolean; reversalJournalId: string; entryNumber: string }> {
  const { data: origForLock } = await db('journal_entries')
    .select('entry_date')
    .eq('tenant_id', params.tenantId)
    .eq('source_type', 'sales_orders')
    .eq('source_id', params.saleId)
    .eq('entry_type', 'sale')
    .not('is_voided', 'is', true)
    .is('reversed_by', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (origForLock) {
    await assertPeriodOpen(params.tenantId, (origForLock as { entry_date: string }).entry_date)
  }

  const { data: rpcResult, error: rpcError } = await rpc('cancel_sale_atomic', {
    p_tenant_id: params.tenantId,
    p_sale_id:   params.saleId,
    p_user_id:   params.userId ?? null,
    p_reason:    params.reason,
    p_idempotency_key: params.idempotencyKey ?? null,
  })
  if (rpcError) throw new Error(`Cancellation failed: ${rpcError.message}`)
  if (!rpcResult?.cancelled) throw new Error('cancel_sale_atomic returned unexpected result')

  return {
    cancelled: true,
    reversalJournalId: rpcResult.reversal_journal_id,
    entryNumber: rpcResult.reversal_journal_number,
  }
}

